import React, { useState } from "react";

import { ApiErrors } from "common/api-errors/errors";
import { CommonButton, UserInfoField } from "common/components";
import { CommonErrorDialog } from "common/components/CommonErrorDialog";
import { ContentWrapper } from "components/ContentWrapper";
import { Formik, Form } from "formik";
import { GetCurrentUser, LogIn } from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Typography,
  Container,
  LinearProgress,
  Grid,
  isWidthDown,
  withWidth,
} from "@material-ui/core";

import "./Login.scss";

interface Values {
  email: string;
  password: string;
}

function Login({ width }: any) {
  //State
  const [error, setError] = useState("");

  //Hooks
  const history = useHistory();
  const { t } = useTranslation();

  const { execute: login } = useCustomQuery(
    LogIn({
      onError: (error: ApiErrors) => {
        if (error === ApiErrors.USER_INVALID_CREDENTIALS) {
          setError(t("Wrong email or password"));
        }
      },
    })
  );

  //Actions
  useCustomQuery(
    GetCurrentUser({
      onSuccess: () => {
        history.push("/offerings");
      },
    })
  );

  //Options
  const initialValues = {
    email: "",
    password: "",
  };

  //Validation
  const onValidate = (values: Values) => {
    const errors: Partial<Values> = {};

    if (!values.email) {
      errors.email = t("Required");
    }

    if (!values.password) {
      errors.password = t("Required");
    }

    return errors;
  };

  const onSubmit = async (values: Values) => {
    await login({
      variables: {
        email: values.email,
        password: values.password,
      },
    });
  };

  return (
    <ContentWrapper paddingTop="0px">
      <Formik
        initialValues={initialValues}
        validate={onValidate}
        onSubmit={onSubmit}
      >
        {({
          submitForm,
          isSubmitting,
          setFieldValue,
          values,
          errors,
          touched,
        }) => {
          const props = {
            values,
            errors,
            touched,
            setFieldValue,
            disableImmutable: false,
          };

          return (
            <Form className="login">
              <Container
                className={
                  isWidthDown("xs", width) ? "container--mobile" : "container"
                }
              >
                <Grid container spacing={6}>
                  <Grid item xs={12}>
                    <Typography className="title" color="primary">
                      {t("Log in")}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <UserInfoField id="email" {...props} />
                  </Grid>

                  <Grid item xs={12}>
                    <UserInfoField id="password" {...props} />
                  </Grid>

                  <Grid item xs={12}>
                    {isSubmitting ? (
                      <LinearProgress />
                    ) : (
                      <CommonButton
                        onClick={submitForm}
                        isSubmitting={isSubmitting}
                        values={values}
                        errors={errors}
                        touched={touched}
                        text={t("Log in")}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className="text">
                      {t("New to FinPro?")}{" "}
                      <span
                        className="text--link"
                        onClick={() => history.push("/authentication/register")}
                      >
                        {t("Register now")}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Container>
            </Form>
          );
        }}
      </Formik>

      <CommonErrorDialog error={error} setError={setError} />
    </ContentWrapper>
  );
}

export default withWidth()(Login);
